import { useState } from 'react';
import { Stack, Table, Text, Badge, Paper, Loader, Center, Group, Button } from '@mantine/core';
import { IconTool, IconCalendar, IconClock, IconPlus } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { formatDate, formatCurrency } from '@/lib/utils';
import { MaintenanceForm } from './MaintenanceForm';
import type { Asset, MaintenanceRecord } from '@/types/models';

interface AssetMaintenanceProps {
  asset: Asset;
}

async function fetchAssetMaintenance(assetId: string) {
  const { data, error } = await supabase
    .from('asset_maintenance')
    .select('*')
    .eq('asset_id', assetId)
    .order('date_completed', { ascending: false });

  if (error) throw error;
  return data as MaintenanceRecord[];
}

const statusColors: Record<string, string> = {
  completed: 'green',
  pending: 'yellow',
  scheduled: 'blue',
  overdue: 'red',
};

export function AssetMaintenance({ asset }: AssetMaintenanceProps) {
  const [formOpened, setFormOpened] = useState(false);
  const { data: maintenanceRecords = [], isLoading } = useQuery({
    queryKey: ['asset-maintenance', asset.id],
    queryFn: () => fetchAssetMaintenance(asset.id),
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="md" />
      </Center>
    );
  }

  if (maintenanceRecords.length === 0) {
    return (
      <>
        <Stack gap="md">
          <Group justify="flex-end">
            <Button leftSection={<IconPlus size={16} />} onClick={() => setFormOpened(true)}>
              Add Maintenance Record
            </Button>
          </Group>
          <Paper p="xl" withBorder>
            <Stack align="center" gap="xs">
              <IconTool size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />
              <Text c="dimmed">No maintenance records for this asset</Text>
            </Stack>
          </Paper>
        </Stack>
        <MaintenanceForm opened={formOpened} onClose={() => setFormOpened(false)} assetId={asset.id} />
      </>
    );
  }

  return (
    <>
      <Stack gap="md">
        <Group justify="flex-end">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setFormOpened(true)}>
            Add Maintenance Record
          </Button>
        </Group>
        <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Title</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Date Completed</Table.Th>
            <Table.Th>Cost</Table.Th>
            <Table.Th>Next Scheduled</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {maintenanceRecords.map((record) => (
            <Table.Tr key={record.id}>
              <Table.Td>
                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    {record.title}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {record.details}
                  </Text>
                </Stack>
              </Table.Td>
              <Table.Td>
                <Badge
                  size="sm"
                  color={statusColors[record.status.toLowerCase()] || 'gray'}
                  variant="light"
                >
                  {record.status}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <IconCalendar size={14} />
                  <Text size="sm">{formatDate(record.date_completed)}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatCurrency(record.cost)}</Text>
              </Table.Td>
              <Table.Td>
                {record.repeating && record.next_maintenance_date ? (
                  <Group gap="xs">
                    <IconClock size={14} />
                    <Stack gap={2}>
                      <Text size="sm">{formatDate(record.next_maintenance_date)}</Text>
                      <Text size="xs" c="dimmed">
                        {record.frequency}
                      </Text>
                    </Stack>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    -
                  </Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Stack>
      <MaintenanceForm opened={formOpened} onClose={() => setFormOpened(false)} assetId={asset.id} />
    </>
  );
}
